import dayjs from "dayjs";
import { useTeamStore } from "../../../../stores/teamStore";

/**
 * Normalizes a filter condition boundary from API (string ISO, Date, epoch ms) to an ISO string
 * so we can read YYYY-MM-DD without timezone drift from full instant parsing.
 */
const filterConditionBoundaryToIsoString = (value) => {
    if (value == null) return null;
    if (typeof value === "string") return value;
    if (typeof value === "number" && Number.isFinite(value)) return new Date(value).toISOString();
    if (value instanceof Date) return value.toISOString();
    if (typeof value === "object" && typeof value.toISOString === "function") return value.toISOString();
    return String(value);
};

/** Prefix used in UI for phone/org member channel values. */
export const PHONE_CHANNEL_PREFIX = "_phone___";

/** Prefix used in selectedChannels for team IDs so ConversationPage sends associatedTeams, not rightServiceId. */
export const TEAM_CHANNEL_PREFIX = "_team___";

/** Channel filter mode: filter by channels (rightServiceId) or teams (associatedTeams). */
export const CHANNEL_FILTER_TYPE = Object.freeze({
    CHANNEL: "channel",
    TEAM: "team",
});

// eslint-disable-next-line no-template-curly-in-string
export const PLACEHOLDER_CURRENT_OSYNC_USER_ID = "${currentOsyncUserId}";
export const PLACEHOLDER_ASSIGNED_OWNER = "${assignedOwnerIds}";

/**
 * Converts a date range [start, end] to ISO strings for the createdTime/messageTime filter.
 * Uses the selected calendar date as UTC so the stored value matches what the user picked.
 * Shared by both "apply filter" (ConversationPage) and "save as view" (buildFilterDefinitionForSave).
 *
 * @param {[dayjs.Dayjs, dayjs.Dayjs]} range - [startDate, endDate] from the date picker
 * @returns {{ startIso: string, endIso: string } | null} - ISO strings, or null if invalid
 */
export const dateRangeToFilterIsoStrings = (range) => {
    if (!Array.isArray(range) || range.length < 2) return null;
    const [start, end] = range;
    const startDayjs = dayjs(start);
    const endDayjs = dayjs(end);
    if (!startDayjs.isValid() || !endDayjs.isValid()) return null;
    const startIso = `${startDayjs.format("YYYY-MM-DD")}T00:00:00.000Z`;
    const endIso = `${endDayjs.format("YYYY-MM-DD")}T23:59:59.999Z`;
    return { startIso, endIso };
};

/**
 * Parses saved BETWEEN ISO values back to dayjs for the RangePicker.
 * Uses the calendar date (YYYY-MM-DD) from each string, not the full instant — otherwise
 * end-of-day UTC (e.g. …T23:59:59.999Z on the 23rd) becomes the next local calendar day in
 * timezones ahead of UTC, and each save/edit cycle shifts dates forward.
 *
 * @param {string} startIso
 * @param {string} endIso
 * @returns {[import("dayjs").Dayjs, import("dayjs").Dayjs] | null}
 */
export const filterIsoRangeToDayjsRange = (startIso, endIso) => {
    const startStr = filterConditionBoundaryToIsoString(startIso);
    const endStr = filterConditionBoundaryToIsoString(endIso);
    if (typeof startStr !== "string" || typeof endStr !== "string") return null;
    const startPart = startStr.slice(0, 10);
    const endPart = endStr.slice(0, 10);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(startPart) || !/^\d{4}-\d{2}-\d{2}$/.test(endPart)) return null;
    const startDayjs = dayjs(startPart);
    const endDayjs = dayjs(endPart);
    if (!startDayjs.isValid() || !endDayjs.isValid()) return null;
    return [startDayjs, endDayjs];
};

/**
 * Builds the placeholder-to-value map for the current user context.
 * Mirrors backend FilterDefinitionPlaceholderResolver.buildPlaceholderMap().
 * To add a new placeholder, define the constant above and add one entry here.
 */
export const buildPlaceholderMap = (appProperties) => {
    const { currentUserTeams } = useTeamStore.getState();
    const map = {};
    const osyncUserId = appProperties?.currentUser?.osyncUserId;
    if (osyncUserId) {
        map[PLACEHOLDER_CURRENT_OSYNC_USER_ID] = osyncUserId;
    }
    if (osyncUserId && currentUserTeams?.length > 0) {
        map[PLACEHOLDER_ASSIGNED_OWNER] = [osyncUserId, ...currentUserTeams.map((team) => team.teamId)];
    } else if (osyncUserId) {
        map[PLACEHOLDER_ASSIGNED_OWNER] = [osyncUserId];
    }
    return map;
};

/**
 * Single source of truth for all filter fields — UI rendering, API condition building,
 * inbox state mapping, store sync, options, defaults, and API-to-UI parsing.
 *
 * To add a new filter field, add one entry here. Everything else derives from it.
 *
 * Property reference:
 *   fieldId, module, displayName, fieldName  – identity
 *   uiComponent          – renderer type (TREESELECT, MULTI_SELECT, CHECKBOX, DATE_RANGE, ASSIGNEE_PICKER)
 *   visible              – false hides from filter UI (apply-only fields)
 *   uiProps              – placeholder, checkedValue, etc. passed to the renderer
 *   defaultValue         – value when filter is reset ([] for multi, null for date, etc.)
 *   storeKey             – listViewStore key for syncing (e.g. "selectedChannels")
 *   tabDisabledKey       – when present in tabConfig.disabledFilters, this field is hidden
 *   optionsKey           – key on the context object passed to getOptionsForField (e.g. "services")
 *   staticOptions        – function returning static options when they don't come from context
 *   stateKey             – key on InboxFilterState (apply flow)
 *   endStateKey          – second state key for range values
 *   apiField             – API condition field name when it differs from fieldName
 *   stateValueTransform  – transform raw state value before building condition
 *   dateFieldOverride    – when true, use state.dateField as the API field name if present
 *   api.operator         – DynamoDB filter operator
 *   api.transform        – (uiValues) => conditions[] for the save flow; may produce different API fields
 *                          (e.g. rightServiceId vs orgMember) based on value format
 *   parseFromApi         – (condition) => { fieldName, value, channelFilterType? } for the edit flow (API → UI).
 *                          channelFilterType ("channel" | "team") affects rightServiceId interpretation.
 */
export const OSYNC_FILTER_FIELDS = [
    {
        fieldId: "1",
        module: "contacts",
        displayName: "Status",
        fieldName: "conversationStatusType",
        uiComponent: "MULTI_SELECT",
        visible: true,
        stateKey: "conversationStatusFilter",
        defaultValue: [],
        storeKey: "selectedConversationStatus",
        tabDisabledKey: "status",
        staticOptions: () => {
            const statuses = ["open", "closed"];
            return statuses.map((s) => ({
                key: s,
                status: s,
                label: s.charAt(0).toUpperCase() + s.slice(1),
                value: s,
            }));
        },
        api: {
            operator: "IN",
            transform: (values) => {
                if (!Array.isArray(values) || values.length === 0) return [];
                const statusCodes = values.filter((v) => v !== "unread");
                if (statusCodes.length === 0) return [];
                return [{ field: "conversationStatusType", operator: "IN", value: statusCodes }];
            },
        },
        parseFromApi: (condition) => {
            if (condition.field === "conversationStatusType") {
                const raw = condition.value;
                const value = Array.isArray(raw) ? raw.filter((v) => v != null && typeof v === "string") : raw;
                return { fieldName: "conversationStatusType", value };
            }
            return null;
        },
    },
    {
        fieldId: "1b",
        module: "contacts",
        displayName: "Unread",
        title: "Read Status",
        fieldName: "readStatus",
        uiComponent: "CHECKBOX",
        visible: true,
        defaultValue: "all",
        storeKey: "selectedReadStatus",
        tabDisabledKey: "readStatus",
        uiProps: { checkedValue: "unread", uncheckedValue: "all", dataCy: "inbox-filter-unread-only" },
        api: {
            transform: (value) => {
                if (value === "unread") {
                    return [
                        {
                            field: "userUnreadCountMap",
                            operator: "IN",
                            value: [PLACEHOLDER_CURRENT_OSYNC_USER_ID],
                        },
                    ];
                }
                return [];
            },
        },
        parseFromApi: (condition) => {
            if (condition.field === "userUnreadCountMap") {
                return { fieldName: "readStatus", value: "unread" };
            }
            return null;
        },
    },
    {
        fieldId: "2",
        module: "contacts",
        displayName: "Assigned To",
        fieldName: "associatedUsers",
        uiComponent: "ASSIGNEE_PICKER",
        visible: true,
        stateKey: "contactOwnerEntityIds",
        apiField: "contactOwnerEntityId",
        defaultValue: [],
        storeKey: "selectedAssignees",
        tabDisabledKey: "assignees",
        optionsKey: "assigneeOptions",
        uiProps: { placeholder: "Select Assignees" },
        api: { operator: "IN" },
        parseFromApi: (condition) => {
            if (condition.field === "associatedUsers" || condition.field === "contactOwnerEntityId") {
                return { fieldName: "associatedUsers", value: condition.value };
            }
            return null;
        },
    },
    {
        fieldName: "userUnreadCountMap",
        visible: false,
        stateKey: "unreadUserId",
        stateValueTransform: (v) => (v ? [v] : null),
        api: { operator: "IN" },
    },
    {
        fieldId: "3",
        module: "contacts",
        displayName: "Channel",
        fieldName: "rightServiceId",
        uiComponent: "TREESELECT",
        visible: true,
        stateKey: "serviceIds",
        defaultValue: [],
        storeKey: "selectedChannels",
        optionsKey: "services",
        uiProps: { placeholder: "All Channels" },
        channelFilterTypeOptions: [
            { label: "Channels", value: CHANNEL_FILTER_TYPE.CHANNEL },
            { label: "Teams", value: CHANNEL_FILTER_TYPE.TEAM },
        ],
        channelFilterTypeGroupLabel: "Channel or Team",
        api: {
            operator: "IN",
            transform: (values) => {
                if (!Array.isArray(values) || values.length === 0) return [];
                const channelUuids = [];
                const orgMemberPhones = [];
                for (const selectedChannelValue of values) {
                    const asString = String(selectedChannelValue);
                    if (asString.includes(PHONE_CHANNEL_PREFIX)) {
                        orgMemberPhones.push(asString.replace(/_phone___/g, ""));
                    } else {
                        channelUuids.push(selectedChannelValue);
                    }
                }
                const apiChannelFilterConditions = [];
                if (channelUuids.length > 0) {
                    apiChannelFilterConditions.push({ field: "rightServiceId", operator: "IN", value: channelUuids });
                }
                if (orgMemberPhones.length > 0) {
                    apiChannelFilterConditions.push({ field: "orgMember", operator: "IN", value: orgMemberPhones });
                }
                if (apiChannelFilterConditions.length === 0) return [];
                if (apiChannelFilterConditions.length === 1) return apiChannelFilterConditions;
                return [{ groupOperator: "OR", conditions: apiChannelFilterConditions }];
            },
        },
        parseFromApi: (condition) => {
            if (condition.field === "rightServiceId") {
                return {
                    fieldName: "rightServiceId",
                    value: condition.value,
                    channelFilterType: CHANNEL_FILTER_TYPE.CHANNEL,
                };
            }
            if (condition.field === "orgMember") {
                return {
                    fieldName: "rightServiceId",
                    value: condition.value?.map((v) => (v && typeof v === "string" ? PHONE_CHANNEL_PREFIX + v : v)),
                    channelFilterType: CHANNEL_FILTER_TYPE.CHANNEL,
                };
            }
            if (condition.field === "associatedTeams") {
                return {
                    fieldName: "rightServiceId",
                    value: condition.value,
                    channelFilterType: CHANNEL_FILTER_TYPE.TEAM,
                };
            }
            return null;
        },
    },
    {
        fieldName: "orgMember",
        apiField: "orgMember",
        visible: false,
        stateKey: "orgMembers",
        api: { operator: "IN" },
    },
    {
        fieldId: "4",
        module: "contacts",
        fieldName: "associatedTeams",
        uiComponent: "MULTI_SELECT",
        visible: false,
        stateKey: "associatedTeamIds",
        defaultValue: [],
        api: {
            operator: "CONTAINS",
            transform: (values) => {
                if (!Array.isArray(values) || values.length === 0) return [];
                return [{ field: "associatedTeams", operator: "CONTAINS", value: values }];
            },
        },
    },

    {
        fieldId: "5",
        module: "contacts",
        displayName: "Flag",
        fieldName: "userFlagColorMap",
        uiComponent: "MULTI_SELECT",
        visible: true,
        stateKey: "userFlagColorMap",
        defaultValue: [],
        storeKey: "selectedFlagFilter",
        staticOptions: () => {
            const colors = ["red", "orange", "yellow", "green"];
            return colors.map((c) => ({
                key: c,
                color: c,
                label: c.charAt(0).toUpperCase() + c.slice(1),
                value: c,
            }));
        },
        api: { operator: "IN" },
        parseFromApi: (condition) => {
            if (condition.field === "userFlagColorMap") {
                const raw = condition.value;
                const value = Array.isArray(raw) ? raw.filter((v) => v != null && typeof v === "string") : raw;
                return { fieldName: "userFlagColorMap", value };
            }
            return null;
        },
    },
    {
        fieldId: "6",
        module: "contacts",
        displayName: "Date Range",
        fieldName: "createdTime",
        uiComponent: "DATE_RANGE",
        visible: true,
        stateKey: "startDateTime",
        endStateKey: "endDateTime",
        apiField: "messageTime",
        dateFieldOverride: true,
        defaultValue: null,
        storeKey: "selectedDateRange",
        api: {
            operator: "BETWEEN",
            transform: (values) => {
                const converted = dateRangeToFilterIsoStrings(values);
                if (!converted) return [];
                return [
                    {
                        field: "createdTime",
                        operator: "BETWEEN",
                        value: [converted.startIso, converted.endIso],
                    },
                ];
            },
        },
        parseFromApi: (condition) => {
            if (
                (condition.field === "createdTime" || condition.field === "messageTime") &&
                condition.operator === "BETWEEN" &&
                Array.isArray(condition.value) &&
                condition.value.length >= 2
            ) {
                const range = filterIsoRangeToDayjsRange(
                    filterConditionBoundaryToIsoString(condition.value[0]),
                    filterConditionBoundaryToIsoString(condition.value[1])
                );
                if (!range) return null;
                return { fieldName: "createdTime", value: range };
            }
            return null;
        },
    },
];

/** All visible filter fields that parse API conditions back to UI values. */
const PARSEABLE_FIELDS = OSYNC_FILTER_FIELDS.filter((f) => f.parseFromApi);

/** API field names each parseable field can handle (built once). */
const API_FIELD_TO_PARSER = (() => {
    const map = {};
    for (const field of PARSEABLE_FIELDS) {
        const apiFields = [field.fieldName, field.apiField].filter(Boolean);
        for (const af of apiFields) {
            map[af] = field;
        }
    }
    map["orgMember"] = PARSEABLE_FIELDS.find((f) => f.fieldName === "rightServiceId");
    map["associatedTeams"] = PARSEABLE_FIELDS.find((f) => f.fieldName === "rightServiceId");
    map["userUnreadCountMap"] = PARSEABLE_FIELDS.find((f) => f.fieldName === "readStatus");
    return map;
})();

const resolvePlaceholdersInValue = (value, placeholderMap) => {
    if (!placeholderMap || !Array.isArray(value)) return value;
    return value.flatMap((v) => {
        if (typeof v !== "string" || placeholderMap[v] === undefined) return [v];
        const resolvedPlaceholderValue = placeholderMap[v];
        return Array.isArray(resolvedPlaceholderValue) ? resolvedPlaceholderValue : [resolvedPlaceholderValue];
    });
};

/**
 * Parses a saved filterDefinition back into tempFilterValues for pre-filling the filter form.
 * Driven entirely by each field's parseFromApi function.
 *
 * @param {object} filterDefinition - the saved filter definition from the API
 * @param {object} [placeholderMap] - optional map of placeholder tokens to resolved values,
 *   e.g. { "${currentOsyncUserId}": "actual-user-id" }
 */
export const parseFilterDefinitionToTempValues = (filterDefinition, placeholderMap) => {
    const result = {};
    if (!filterDefinition?.conditions?.length) return result;

    for (const topLevelCondition of filterDefinition.conditions) {
        if (topLevelCondition?.conditions?.length && topLevelCondition.groupOperator === "OR") {
            const isOrGroupOfSingleTeamContainsEach = topLevelCondition.conditions.every(
                (nestedCondition) =>
                    nestedCondition?.field === "associatedTeams" &&
                    nestedCondition?.operator === "CONTAINS" &&
                    Array.isArray(nestedCondition?.value) &&
                    nestedCondition.value.length === 1
            );
            if (isOrGroupOfSingleTeamContainsEach) {
                result.rightServiceId = topLevelCondition.conditions.map((nestedCondition) =>
                    resolvePlaceholdersInValue(nestedCondition.value, placeholderMap)?.[0]
                );
                result.channelFilterType = CHANNEL_FILTER_TYPE.TEAM;
                continue;
            }

            const rightServiceIdInCondition = topLevelCondition.conditions.find(
                (nestedCondition) =>
                    nestedCondition?.field === "rightServiceId" &&
                    String(nestedCondition?.operator || "").toUpperCase() === "IN" &&
                    Array.isArray(nestedCondition?.value) &&
                    !(nestedCondition?.conditions?.length > 0)
            );
            const orgMemberInCondition = topLevelCondition.conditions.find(
                (nestedCondition) =>
                    nestedCondition?.field === "orgMember" &&
                    String(nestedCondition?.operator || "").toUpperCase() === "IN" &&
                    Array.isArray(nestedCondition?.value) &&
                    !(nestedCondition?.conditions?.length > 0)
            );
            if (rightServiceIdInCondition && orgMemberInCondition) {
                const resolvedRightServiceIdValues =
                    resolvePlaceholdersInValue(rightServiceIdInCondition.value, placeholderMap) ?? [];
                const resolvedOrgMemberValues =
                    resolvePlaceholdersInValue(orgMemberInCondition.value, placeholderMap) ?? [];
                result.rightServiceId = [
                    ...resolvedRightServiceIdValues.filter((integrationOrChannelId) => integrationOrChannelId != null),
                    ...resolvedOrgMemberValues
                        .filter((orgMemberValue) => orgMemberValue != null)
                        .map((orgMemberValue) =>
                            typeof orgMemberValue === "string"
                                ? PHONE_CHANNEL_PREFIX + orgMemberValue
                                : orgMemberValue
                        ),
                ];
                result.channelFilterType = CHANNEL_FILTER_TYPE.CHANNEL;
                continue;
            }

            continue;
        }

        const conditionWithResolvedPlaceholders = {
            ...topLevelCondition,
            value: resolvePlaceholdersInValue(topLevelCondition?.value, placeholderMap),
        };

        const filterFieldParser = API_FIELD_TO_PARSER[conditionWithResolvedPlaceholders?.field];
        if (!filterFieldParser) continue;

        const parsedFilterField = filterFieldParser.parseFromApi(conditionWithResolvedPlaceholders);
        if (!parsedFilterField) continue;

        result[parsedFilterField.fieldName] = parsedFilterField.value;
        if (parsedFilterField.channelFilterType) {
            result.channelFilterType = parsedFilterField.channelFilterType;
        }
    }
    return result;
};
